import { useState, useEffect } from 'react';
import { RefreshCw, Heart, BookOpen, Quote, Loader2, Sparkles } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ResetTab = () => {
  const [content, setContent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [fadeIn, setFadeIn] = useState(false);

  const fetchContent = async (refresh = false) => {
    try {
      if (refresh) {
        setIsRefreshing(true);
        setFadeIn(false);
      } else {
        setIsLoading(true);
      }
      
      const endpoint = refresh ? `${API}/reset/refresh` : `${API}/reset/daily`;
      const response = await axios.get(endpoint);
      
      // Small delay for smooth transition
      await new Promise(resolve => setTimeout(resolve, 300));
      
      setContent(response.data);
      setFadeIn(true);
    } catch (err) {
      console.error('Error fetching reset content:', err);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center space-y-4">
          <Sparkles className="w-12 h-12 mx-auto text-amber-400/50 animate-pulse" />
          <p className="text-zinc-500 text-sm">Preparing your moment of peace...</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="space-y-6 pb-8" 
      data-testid="reset-tab"
    >
      {/* Header */}
      <div className="text-center py-4">
        <h2 className="font-heading text-2xl font-bold text-zinc-100 mb-2">
          Reset
        </h2>
        <p className="text-zinc-500 text-sm">
          Take a breath. Find your center.
        </p>
      </div>

      {/* Content Container with Fade Animation */}
      <div className={`space-y-6 transition-all duration-700 ${fadeIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        
        {/* Daily Encouragement */}
        <section className="relative overflow-hidden rounded-xl bg-gradient-to-br from-zinc-900 via-zinc-900 to-zinc-800 border border-zinc-800/50">
          {/* Subtle ambient glow */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-500/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
          
          <div className="relative p-6">
            <div className="flex items-center gap-2 mb-4">
              <Heart className="w-4 h-4 text-rose-400" />
              <span className="text-xs font-medium text-rose-400 uppercase tracking-wider">Daily Encouragement</span>
            </div>
            <p className="text-zinc-200 text-base leading-relaxed" data-testid="encouragement-text">
              {content?.encouragement}
            </p>
          </div>
        </section>

        {/* Quote of the Day */}
        <section className="relative overflow-hidden rounded-xl bg-gradient-to-br from-zinc-900 to-zinc-900/80 border border-zinc-800/50">
          <div className="absolute top-4 left-4 text-6xl text-zinc-800/50 font-serif leading-none">"</div>
          
          <div className="relative p-6 pt-10">
            <div className="flex items-center gap-2 mb-4">
              <Quote className="w-4 h-4 text-amber-400" />
              <span className="text-xs font-medium text-amber-400 uppercase tracking-wider">Quote of the Day</span>
            </div>
            <blockquote className="mb-4">
              <p className="text-zinc-100 text-lg leading-relaxed italic" data-testid="quote-text">
                "{content?.quote?.text}"
              </p>
            </blockquote>
            <cite className="flex items-center gap-2 not-italic">
              <span className="w-8 h-px bg-zinc-700"></span>
              <span className="text-zinc-400 text-sm" data-testid="quote-author">
                {content?.quote?.author}
              </span>
            </cite>
          </div>
        </section>

        {/* Bible Verse */}
        <section className="relative overflow-hidden rounded-xl bg-gradient-to-br from-zinc-900 via-zinc-900 to-indigo-950/30 border border-zinc-800/50">
          {/* Subtle cross/light pattern */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-amber-500/5 to-transparent rounded-full blur-2xl" />
          
          <div className="relative p-6">
            <div className="flex items-center gap-2 mb-4">
              <BookOpen className="w-4 h-4 text-sky-400" />
              <span className="text-xs font-medium text-sky-400 uppercase tracking-wider">Verse of the Day</span>
            </div>
            <p className="text-zinc-200 text-base leading-relaxed mb-3" data-testid="verse-text">
              {content?.verse?.text}
            </p>
            <p className="text-zinc-500 text-sm font-medium" data-testid="verse-reference">
              — {content?.verse?.reference}
            </p>
          </div>
        </section>

        {/* Refresh Button */}
        <div className="flex justify-center pt-4">
          <button
            onClick={() => fetchContent(true)}
            disabled={isRefreshing}
            className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-zinc-800/50 border border-zinc-700/50 text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800 hover:border-zinc-600 transition-all duration-300 disabled:opacity-50"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            <span className="text-sm">
              {isRefreshing ? 'Finding peace...' : 'New inspiration'}
            </span>
          </button>
        </div>

        {/* Return Message */}
        <div className="text-center py-6">
          <p className="text-zinc-600 text-xs">
            Content refreshes daily at midnight
          </p>
          <p className="text-zinc-500 text-sm mt-2">
            ✨ Return tomorrow for a new message
          </p>
        </div>

      </div>
    </div>
  );
};
